import {
  COVERAGE_FORM,
  COVERAGE_NAMES,
  ERROR_MESSAGES,
} from "@platform/rfp-core";

import {
  validateCurrentCommission,
  validateCurrentCommissionDetail,
  validateRequestedCommission,
  validateRequestedCommissionDetail,
} from "./commissionValidation";

const {
  ACCIDENT,
  VISION,
  CANCER,
  CI,
  HOSPITAL,
  WHOLE_LIFE,
  LTD,
  FMLA,
  BASIC_LIFE,
  VOLUNTARY_LIFE,
  STD,
  EAP,
  DENTAL,
  DENTAL_PPO,
  DENTAL_DHMO,
  INDIVIDUAL_DISABILITY,
  SDL,
} = COVERAGE_NAMES;

const {
  CURRENT_COMMISSION,
  REQUESTED_COMMISSION,
  CURRENT_COMMISSION_DETAIL,
  REQUESTED_COMMISSION_DETAIL,
  CONTRIBUTION_STRUCTURE,
  FUNDING_ARRANGEMENT,
  ENROLLED_LIVES,
  RATE_TYPE,
  ENROLLED_W2_EMPLOYEES,
  ADA,
  STATES_KEY,
  PPO,
  INDEMNITY,
  SINGLE_OPTION_PPO,
  DUAL_OPTION_PPO_1,
  DUAL_OPTION_PPO_2,
  DUAL_OPTION_PPO,
  CONTRIBUTORY,
  CONTRIBUTION_TYPE,
  CONTRIBUTION_VALUE,
  PLAN_TYPE,
  DHMO,
  PLANS,
  FICA_OPTION,
  FICA_BILLABLE,
  FICA_MATCHING_YES,
  ALTERNATE_PLANS,
  ADDITIONAL_NOTES,
} = COVERAGE_FORM;

const {
  REQUIRED_FIELD,
  REQUIRED_OPTION,
  REQUIRED_STATE,
  CONTRIBUTION_VALUE_VALIDATION,
} = ERROR_MESSAGES;

export const validation = {
  [PLAN_TYPE]: (value) => !value[PLAN_TYPE] && REQUIRED_OPTION,
  [CURRENT_COMMISSION]: validateCurrentCommission,
  [REQUESTED_COMMISSION]: validateRequestedCommission,
  [CURRENT_COMMISSION_DETAIL]: validateCurrentCommissionDetail,
  [REQUESTED_COMMISSION_DETAIL]: validateRequestedCommissionDetail,
  [CONTRIBUTION_STRUCTURE]: (value) => {
    if (!value[CONTRIBUTION_STRUCTURE]) {
      return REQUIRED_OPTION;
    }

    if (value[CONTRIBUTION_STRUCTURE] === CONTRIBUTORY) {
      const hasType = value[CONTRIBUTION_TYPE];
      const hasValue =
        value[CONTRIBUTION_VALUE] && value[CONTRIBUTION_VALUE].trim();

      if (!hasType || !hasValue) {
        return CONTRIBUTION_VALUE_VALIDATION;
      }
    }

    return false;
  },
  [CONTRIBUTION_TYPE]: (value) => {
    if (value[CONTRIBUTION_STRUCTURE] === CONTRIBUTORY) {
      return !value[CONTRIBUTION_TYPE] && REQUIRED_OPTION;
    }
    return false;
  },
  [CONTRIBUTION_VALUE]: (value) => {
    if (
      value[CONTRIBUTION_STRUCTURE] === CONTRIBUTORY &&
      value[CONTRIBUTION_TYPE]
    ) {
      return (
        (!value[CONTRIBUTION_VALUE] || !value[CONTRIBUTION_VALUE].trim()) &&
        REQUIRED_FIELD
      );
    }
    return false;
  },
  [FUNDING_ARRANGEMENT]: (value) =>
    !value[FUNDING_ARRANGEMENT] && REQUIRED_OPTION,
  [FICA_OPTION]: (value) => !value[FICA_OPTION] && REQUIRED_OPTION,
  [FICA_BILLABLE]: (value) => {
    // Billable is required only when FICA Matching is Yes; otherwise it is null.
    if (value[FICA_OPTION] === FICA_MATCHING_YES) {
      return !value[FICA_BILLABLE] && REQUIRED_OPTION;
    }
    return false;
  },
  [ENROLLED_LIVES]: (value) => !value[ENROLLED_LIVES]?.trim() && REQUIRED_FIELD,
  [RATE_TYPE]: (value) => !value[RATE_TYPE] && REQUIRED_OPTION,
  [ENROLLED_W2_EMPLOYEES]: (value) =>
    !value[ENROLLED_W2_EMPLOYEES]?.trim() && REQUIRED_FIELD,
  [ADA]: (value) => !value[ADA] && REQUIRED_OPTION,
  [STATES_KEY]: (value) => {
    if (!value[STATES_KEY]) return REQUIRED_STATE;
    const states = Object.keys(value[STATES_KEY]);
    if (states.length === 0) return REQUIRED_STATE;
    const stateErrors = states.reduce(
      (acc, key) =>
        !value[STATES_KEY][key].some((v) => !!v)
          ? { ...acc, [key]: REQUIRED_FIELD }
          : acc,
      {},
    );
    return Object.keys(stateErrors).length !== 0 ? stateErrors : false;
  },
};

export const validateField = (fieldName, planData) => {
  if (!validation[fieldName]) return false;
  return validation[fieldName](planData);
};

const SUPPORTED_COVERAGES = [
  DENTAL,
  DENTAL_PPO,
  DENTAL_DHMO,
  VISION,
  BASIC_LIFE,
  ACCIDENT,
  VOLUNTARY_LIFE,
  LTD,
  STD,
  FMLA,
  INDIVIDUAL_DISABILITY,
  SDL,
  EAP,
];

const needsFundingArrangement = (coverageName, planData) =>
  coverageName === VISION ||
  coverageName === STD ||
  (coverageName === DENTAL && planData.name !== DHMO) ||
  coverageName === DENTAL_PPO ||
  coverageName === DENTAL_DHMO;

const buildCoreFieldOrder = (coverageName, planData, isCurrentCoverage) => {
  if (coverageName === FMLA) {
    return [ENROLLED_W2_EMPLOYEES, ADA];
  }

  if (coverageName === SDL) {
    return [...(isCurrentCoverage ? [ENROLLED_LIVES] : []), STATES_KEY];
  }

  const order = [];
  if (isCurrentCoverage && coverageName !== DENTAL_PPO) {
    order.push(ENROLLED_LIVES);
  }
  order.push(CONTRIBUTION_STRUCTURE);
  if (coverageName === LTD) order.push(RATE_TYPE);
  if (needsFundingArrangement(coverageName, planData)) {
    order.push(FUNDING_ARRANGEMENT);
  }
  if (coverageName === STD) order.push(FICA_OPTION, FICA_BILLABLE);
  return order;
};

const buildFieldOrder = (coverageName, planData, isCurrentCoverage) => {
  const order = buildCoreFieldOrder(coverageName, planData, isCurrentCoverage);

  if (coverageName !== EAP) {
    order.push(isCurrentCoverage ? CURRENT_COMMISSION : REQUESTED_COMMISSION);
  }

  order.push(ALTERNATE_PLANS, ADDITIONAL_NOTES);
  return order;
};

export const validatePreviousFields = (
  targetField,
  planData,
  coverageName,
  isCurrentCoverage,
) => {
  if (!SUPPORTED_COVERAGES.includes(coverageName)) {
    return {};
  }

  const fieldOrder = buildFieldOrder(coverageName, planData, isCurrentCoverage);
  const targetIndex = fieldOrder.indexOf(targetField);
  if (targetIndex === -1) return {};

  const errors = {};
  for (let i = 0; i < targetIndex; i++) {
    const fieldName = fieldOrder[i];
    const fieldError = validateField(fieldName, planData);
    if (fieldError) {
      errors[fieldName] = fieldError;
    }
  }

  return errors;
};

export const getFieldOrder = (coverageType) => {
  const baseOrder = [
    PLAN_TYPE,
    CURRENT_COMMISSION,
    REQUESTED_COMMISSION,
    CONTRIBUTION_STRUCTURE,
    CONTRIBUTION_TYPE,
    CONTRIBUTION_VALUE,
    FUNDING_ARRANGEMENT,
    ENROLLED_LIVES,
    RATE_TYPE,
    STATES_KEY,
  ];

  if (coverageType === "DENTAL") {
    return [...baseOrder, ENROLLED_W2_EMPLOYEES, ADA];
  }

  return baseOrder;
};

const commissionValidations = (value) =>
  value.isCurrentCoverage
    ? {
        [CURRENT_COMMISSION]: validation[CURRENT_COMMISSION](value),
        [REQUESTED_COMMISSION_DETAIL]:
          validation[REQUESTED_COMMISSION_DETAIL](value),
      }
    : {
        [REQUESTED_COMMISSION]: validation[REQUESTED_COMMISSION](value),
      };

export const coveragesValidations = {
  [ACCIDENT]: (value) => ({
    ...commissionValidations(value),
  }),
  [VISION]: (value) => ({
    ...commissionValidations(value),
    [CONTRIBUTION_STRUCTURE]: validation[CONTRIBUTION_STRUCTURE](value),
    [FUNDING_ARRANGEMENT]: validation[FUNDING_ARRANGEMENT](value),
  }),
  [CANCER]: (value) => ({
    ...commissionValidations(value),
  }),
  [CI]: (value) => ({
    ...commissionValidations(value),
  }),
  [HOSPITAL]: (value) => ({
    ...commissionValidations(value),
  }),
  [WHOLE_LIFE]: (value) => ({
    ...commissionValidations(value),
  }),
  [BASIC_LIFE]: (value) => ({
    ...(value.isCurrentCoverage && {
      [ENROLLED_LIVES]: validation[ENROLLED_LIVES](value),
    }),
    ...commissionValidations(value),
    [CONTRIBUTION_STRUCTURE]: validation[CONTRIBUTION_STRUCTURE](value),
  }),
  [LTD]: (value) => ({
    ...(value.isCurrentCoverage && {
      [ENROLLED_LIVES]: validation[ENROLLED_LIVES](value),
    }),
    ...commissionValidations(value),
    [CONTRIBUTION_STRUCTURE]: validation[CONTRIBUTION_STRUCTURE](value),
    [RATE_TYPE]: validation[RATE_TYPE](value),
  }),
  [FMLA]: (value) => ({
    ...commissionValidations(value),
    [ENROLLED_W2_EMPLOYEES]: validation[ENROLLED_W2_EMPLOYEES](value),
    [ADA]: validation[ADA](value),
  }),
  [VOLUNTARY_LIFE]: (value) => ({
    ...(value.isCurrentCoverage && {
      [ENROLLED_LIVES]: validation[ENROLLED_LIVES](value),
    }),
    ...commissionValidations(value),
  }),
  [STD]: (value) => ({
    ...(value.isCurrentCoverage && {
      [ENROLLED_LIVES]: validation[ENROLLED_LIVES](value),
    }),
    ...commissionValidations(value),
    [CONTRIBUTION_STRUCTURE]: validation[CONTRIBUTION_STRUCTURE](value),
    [FUNDING_ARRANGEMENT]: validation[FUNDING_ARRANGEMENT](value),
    [FICA_OPTION]: validation[FICA_OPTION](value),
    [FICA_BILLABLE]: validation[FICA_BILLABLE](value),
  }),
  [EAP]: () => true,
  [DENTAL]: (value) => ({
    ...(value.isCurrentCoverage && {
      [ENROLLED_LIVES]: validation[ENROLLED_LIVES](value),
    }),
    ...commissionValidations(value),
    [CONTRIBUTION_STRUCTURE]: validation[CONTRIBUTION_STRUCTURE](value),
    ...([
      PPO,
      INDEMNITY,
      DUAL_OPTION_PPO_1,
      DUAL_OPTION_PPO_2,
      DUAL_OPTION_PPO,
    ].includes(value.name) && {
      [FUNDING_ARRANGEMENT]: validation[FUNDING_ARRANGEMENT](value),
    }),
  }),
  [DENTAL_PPO]: (value) => ({
    ...(value.isCurrentCoverage && {
      [ENROLLED_LIVES]: validation[ENROLLED_LIVES](value),
    }),
    ...commissionValidations(value),
    [CONTRIBUTION_STRUCTURE]: validation[CONTRIBUTION_STRUCTURE](value),
    ...([
      SINGLE_OPTION_PPO,
      DUAL_OPTION_PPO_1,
      DUAL_OPTION_PPO_2,
      DUAL_OPTION_PPO,
    ].includes(value.name) && {
      [FUNDING_ARRANGEMENT]: validation[FUNDING_ARRANGEMENT](value),
    }),
  }),
  [DENTAL_DHMO]: (value) => ({
    ...(value.isCurrentCoverage && {
      [ENROLLED_LIVES]: validation[ENROLLED_LIVES](value),
    }),
    ...commissionValidations(value),
    [CONTRIBUTION_STRUCTURE]: validation[CONTRIBUTION_STRUCTURE](value),
    [FUNDING_ARRANGEMENT]: validation[FUNDING_ARRANGEMENT](value),
  }),
  [INDIVIDUAL_DISABILITY]: (value) => ({
    ...commissionValidations(value),
    [CONTRIBUTION_STRUCTURE]: validation[CONTRIBUTION_STRUCTURE](value),
  }),
  [SDL]: (value) => ({
    ...commissionValidations(value),
    [ENROLLED_LIVES]: validation[ENROLLED_LIVES](value),
    [STATES_KEY]: validation[STATES_KEY](value),
  }),
};

export const createPlanFormChangeWithValidation =
  (currentPlan, coverage, onFormChange, activeTab = 0, planName = null) =>
  ({ name, value }) => {
    const updatedPlan = { ...currentPlan, [name]: value };
    const fieldError = validateField(name, updatedPlan);

    if (name === CONTRIBUTION_TYPE) {
      updatedPlan[CONTRIBUTION_VALUE] = "";
    }

    if (Object.prototype.hasOwnProperty.call(updatedPlan, "displayName")) {
      updatedPlan.displayName = updatedPlan.displayName || "NO_PLAN";
    }

    const newErrors = { ...(currentPlan.errors || {}) };
    if (fieldError) {
      newErrors[name] = fieldError;
    } else {
      delete newErrors[name];
    }

    if (name === CONTRIBUTION_TYPE || name === CONTRIBUTION_VALUE) {
      const contributionStructureError = validateField(
        CONTRIBUTION_STRUCTURE,
        updatedPlan,
      );
      if (contributionStructureError) {
        newErrors[CONTRIBUTION_STRUCTURE] = contributionStructureError;
      } else {
        delete newErrors[CONTRIBUTION_STRUCTURE];
      }
    }

    updatedPlan.errors = newErrors;

    let payload;
    if (coverage.details.plans && Array.isArray(coverage.details.plans)) {
      const plans = coverage.details.plans.map((plan, index) => {
        if (
          (planName && plan.name === planName) ||
          (!planName && index === activeTab)
        ) {
          return updatedPlan;
        }
        return plan;
      });

      payload = {
        ...coverage,
        details: { ...coverage.details, plans },
      };
    } else {
      payload = {
        ...coverage,
        details: {
          ...coverage.details,
          plans: [updatedPlan],
        },
      };
    }

    onFormChange(payload, activeTab, name);
  };

export const createMultipleFieldValidation =
  (currentPlan, coverage, onFormChange, activeTab = 0, planName = null) =>
  (fieldsToValidate) => {
    const newErrors = { ...(currentPlan.errors || {}) };

    Object.keys(fieldsToValidate).forEach((fieldName) => {
      if (fieldsToValidate[fieldName]) {
        newErrors[fieldName] = fieldsToValidate[fieldName];
      } else {
        delete newErrors[fieldName];
      }
    });

    const updatedPlan = { ...currentPlan, errors: newErrors };

    let payload;
    if (coverage.details.plans && Array.isArray(coverage.details.plans)) {
      const plans = coverage.details.plans.map((plan, index) => {
        if (
          (planName && plan.name === planName) ||
          (!planName && index === activeTab)
        ) {
          return updatedPlan;
        }
        return plan;
      });

      payload = {
        ...coverage,
        details: { ...coverage.details, plans },
      };
    } else {
      payload = {
        ...coverage,
        details: {
          ...coverage.details,
          plans: [updatedPlan],
        },
      };
    }

    onFormChange(payload, activeTab);
  };

export const createPreviousFieldsValidation =
  (multipleFieldValidationHandler, currentPlan, coverage, isCurrentCoverage) =>
  (targetField) => {
    const currentPlanData = { ...currentPlan };
    const previousFieldsErrors = validatePreviousFields(
      targetField,
      currentPlanData,
      coverage.name,
      isCurrentCoverage,
    );

    if (Object.keys(previousFieldsErrors).length > 0) {
      multipleFieldValidationHandler(previousFieldsErrors);
    }
  };

export const createValidationHandlers = ({
  currentPlan,
  coverage,
  onFormChange,
  isCurrentCoverage,
  activeTab = 0,
  planName = null,
}) => {
  const handlePlanFormChangeWithValidation = createPlanFormChangeWithValidation(
    currentPlan,
    coverage,
    onFormChange,
    activeTab,
    planName,
  );

  const handleMultipleFieldValidation = createMultipleFieldValidation(
    currentPlan,
    coverage,
    onFormChange,
    activeTab,
    planName,
  );

  const handlePreviousFieldsValidation = createPreviousFieldsValidation(
    handleMultipleFieldValidation,
    currentPlan,
    coverage,
    isCurrentCoverage,
  );

  return {
    handlePlanFormChangeWithValidation,
    handleMultipleFieldValidation,
    handlePreviousFieldsValidation,
  };
};

const updatePlanForDHMO = (plan, index, activeTab, name, value, fieldError) => {
  const planToUpdate = {
    ...plan,
    [CONTRIBUTION_VALUE]:
      name === CONTRIBUTION_TYPE ? "" : plan[CONTRIBUTION_VALUE],
    [name]: value,
  };

  if (index === activeTab) {
    const newErrors = { ...(plan.errors || {}) };
    if (fieldError) {
      newErrors[name] = fieldError;
    } else {
      delete newErrors[name];
    }
    planToUpdate.errors = newErrors;
  }

  return planToUpdate;
};

const updatePlanForNonDHMO = (
  plan,
  index,
  activeTab,
  planName,
  name,
  value,
  fieldError,
) => {
  if (plan.name === planName) {
    if (name === CONTRIBUTION_TYPE) {
      plan[CONTRIBUTION_VALUE] = "";
    }

    const planToUpdate = {
      ...plan,
      [CONTRIBUTION_VALUE]:
        name === CONTRIBUTION_TYPE ? "" : plan[CONTRIBUTION_VALUE],
      [name]: value,
    };

    if (index === activeTab) {
      const newErrors = { ...(plan.errors || {}) };
      if (fieldError) {
        newErrors[name] = fieldError;
      } else {
        delete newErrors[name];
      }
      planToUpdate.errors = newErrors;
    }

    return planToUpdate;
  }
  return plan;
};

const updatePlansForErrors = (
  plans,
  isDHMO,
  activeTab,
  planName,
  newErrors,
) => {
  if (isDHMO) {
    return plans.map((plan, index) => {
      if (index === activeTab) {
        return { ...plan, errors: newErrors };
      }
      return plan;
    });
  }

  return plans.map((plan, index) => {
    if (plan.name === planName && index === activeTab) {
      return { ...plan, errors: newErrors };
    }
    return plan;
  });
};

export const createTabbedFormValidationHandlers = ({
  currentPlan,
  coverage,
  onFormChange,
  isCurrentCoverage,
  activeTab,
  isDHMO = false,
}) => {
  const handlePlanFormChangeWithValidation = ({ name, value }, planName) => {
    const updatedPlan = { ...currentPlan, [name]: value };
    const fieldError = validateField(name, updatedPlan);

    const plans = isDHMO
      ? coverage.details.plans.map((plan, index) =>
          updatePlanForDHMO(plan, index, activeTab, name, value, fieldError),
        )
      : coverage.details.plans.map((plan, index) =>
          updatePlanForNonDHMO(
            plan,
            index,
            activeTab,
            planName,
            name,
            value,
            fieldError,
          ),
        );

    const payload = {
      ...coverage,
      details: { ...coverage.details, [PLANS]: plans },
    };

    onFormChange(payload, activeTab, name);
  };

  const handleMultipleFieldValidation = (fieldsToValidate, planName) => {
    const newErrors = { ...(currentPlan.errors || {}) };

    Object.keys(fieldsToValidate).forEach((fieldName) => {
      if (fieldsToValidate[fieldName]) {
        newErrors[fieldName] = fieldsToValidate[fieldName];
      } else {
        delete newErrors[fieldName];
      }
    });

    const plans = updatePlansForErrors(
      coverage.details.plans,
      isDHMO,
      activeTab,
      planName,
      newErrors,
    );

    const payload = {
      ...coverage,
      details: { ...coverage.details, [PLANS]: plans },
    };

    onFormChange(payload, activeTab);
  };

  const handlePreviousFieldsValidation = (targetField, planName) => {
    const currentPlanData = { ...currentPlan };
    const previousFieldsErrors = validatePreviousFields(
      targetField,
      currentPlanData,
      coverage.name,
      isCurrentCoverage,
    );

    if (Object.keys(previousFieldsErrors).length > 0) {
      handleMultipleFieldValidation(previousFieldsErrors, planName);
    }
  };

  return {
    handlePlanFormChangeWithValidation,
    handleMultipleFieldValidation,
    handlePreviousFieldsValidation,
  };
};
